<template>
    <div class="tx-mobile-overview"
        v-bind:class="{
            'tx-mobile-overview--success': traceStatus === true && !isPending,
            'tx-mobile-overview--failed': traceStatus === false && !isPending,
        }">
        <div v-if="isTickTock"
            class="tx-mobile-overview-icon"
            v-bind:class="{
                'tx-mobile-overview-icon--failed': traceStatus === false,
            }"
        >
            <icon-service />
        </div>
        <div v-else-if="!simplePreview || !isTxSimple"
            class="tx-mobile-overview-icon"
            v-bind:class="{
                'tx-mobile-overview-icon--failed': traceStatus === false,
                'tx-mobile-overview-icon--skeleton': !getIcon || traceStatus === undefined,
            }"
        >
            <component v-if="getIcon" v-bind:is="getIcon" />
        </div>
        <div v-else>
            <template v-if="simplePreview.event === 'nft_transfer' || simplePreview.event === 'nft_purchase'">
                <img
                    v-show="!nftError && simplePreview.meta?.image"
                    v-bind:src="simplePreview.meta?.image"
                    alt=""
                    class="tx-mobile-overview-nft"
                    v-on:error="nftError = true"
                />
                <div v-show="nftError || !simplePreview.meta?.image" class="tx-mobile-overview-nft">
                    <icon-nft />
                </div>
            </template>
            <div class="tx-mobile-overview-swap" v-else-if="simplePreview.event === 'jetton_swap'">
                <ui-round-image
                    class="tx-mobile-overview-swap-logo tx-mobile-overview-swap-logo--left"
                    v-bind:src="simplePreview.meta.image_in" />
                <ui-round-image
                    class="tx-mobile-overview-swap-logo tx-mobile-overview-swap-logo--right"
                    v-bind:src="simplePreview.meta.image_out" />
            </div>
            <div class="tx-mobile-overview-swap" v-else-if="simplePreview.event === 'sent_jetton' || simplePreview.event === 'received_jetton' || simplePreview.event === 'jetton_burn'"    >
                <ui-round-image
                    class="tx-mobile-overview-swap-logo"
                    v-bind:src="simplePreview.meta.image" />
            </div>
            <div v-else-if="simplePreview.event && getIcon"
                class="tx-mobile-overview-icon"
                v-bind:class="{
                    'tx-mobile-overview-icon--failed': traceStatus === false,
                    'tx-mobile-overview-icon--skeleton': !getIcon || traceStatus === undefined,
                }"
            >
                <component v-bind:is="getIcon" />
            </div>
        </div>
    </div>
</template>

<script>
import IconIn from '@img/icons/lucide/arrow-down-left.svg?vue';
import IconOut from '@img/icons/lucide/arrow-up-right.svg?vue';
import IconBurn from '@img/icons/lucide/flame.svg?vue';
import IconJettonMint from '@img/icons/lucide/sparkles.svg?vue';
import IconNftIn from '@img/icons/lucide/image-down.svg?vue';
import IconNftOut from '@img/icons/lucide/image-up.svg?vue';
import IconNftBuy from '@img/icons/lucide/shopping-bag.svg?vue';
import IconNftMint from '@img/icons/lucide/image-plus.svg?vue';
import IconSwap from '@img/icons/lucide/arrow-left-right.svg?vue';
import IconContractDeploy from '@img/icons/lucide/square-dashed-bottom-code.svg?vue';
import IconSmartContractExec from '@img/icons/lucide/code.svg?vue';
import IconStakeIn from '@img/icons/lucide/hand-coins.svg?vue';
import IconStakeOut from '@img/icons/lucide/landmark.svg?vue';
import IconStakePush from '@img/icons/lucide/send.svg?vue';
import IconStakeDeposit from '@img/icons/lucide/layers-plus.svg?vue';
import IconEvaaSupply from '@img/icons/lucide/diamond-plus.svg?vue';
import IconContractCall from '@img/icons/lucide/square-code.svg?vue';
import IconStakeWithdraw from '@img/icons/lucide/layers-arrow-up.svg?vue';
import IconAuctionBid from '@img/icons/lucide/gavel.svg?vue';
import IconSubscribe from '@img/icons/lucide/bell-plus.svg?vue';
import IconUnsubscribe from '@img/icons/lucide/bell-off.svg?vue';
import IconDomainRenew from '@img/icons/lucide/globe.svg?vue';
import IconUpdate from '@img/icons/lucide/refresh-cw.svg?vue';
import IconCheck from '@img/icons/lucide/check.svg?vue';
import IconKeyRound from '@img/icons/lucide/key-round.svg?vue';
import IconUnknown from '@img/icons/lucide/squircle-dashed.svg?vue';

import IconNft from '@img/icons/tonscan/nft-14.svg?inline';
import IconSuccess from '@img/icons/lucide/check-check.svg?inline';
import IconFailed from '@img/icons/lucide/x.svg?inline';
import IconService from '@img/icons/lucide/refresh-cw.svg?inline';
import UiInlineNft from '~/components/address/TxRowInlineNft.vue';

/* eslint-disable "quote-props" */
const iconMap = Object.freeze({
    ton_transfer: IconOut,
    received_gram: IconIn,
    sent_gram: IconOut,
    jetton_transfer: IconOut,
    received_jetton: IconIn,
    sent_jetton: IconOut,
    auction_bid: IconAuctionBid,
    contract_deploy: IconContractDeploy,
    call_contract: IconContractCall,
    deploy_nft: IconNftMint,
    nft_transfer: IconNftOut,
    stake_deposit: IconStakeDeposit,
    renew_dns: IconDomainRenew,
    election_deposit: IconStakeOut,
    election_recover: IconStakeIn,
    jetton_mint: IconJettonMint,
    jetton_burn: IconBurn,
    jetton_swap: IconSwap,
    nft_purchase: IconNftBuy,
    nft_mint: IconNftBuy,
    received_nft: IconNftIn,
    sent_nft: IconNftOut,
    subscribe: IconSubscribe,
    unsubscribe: IconUnsubscribe,
    stake_withdrawal: IconStakeWithdraw,
    stake_withdrawal_request: IconStakeIn,
    change_dns: IconDomainRenew,
    delete_dns: IconDomainRenew,
    dex_deposit_liquidity: IconStakeDeposit,
    dex_withdraw_liquidity: IconStakeWithdraw,
    multisig_execute: IconSmartContractExec,
    multisig_approve: IconCheck,
    multisig_create_order: IconStakePush,
    jvault_stake: IconStakeDeposit,
    jvault_unstake: IconStakeWithdraw,
    jvault_claim: IconStakeIn,
    vesting_add_whitelist: IconUpdate,
    vesting_send_message: IconSmartContractExec,
    evaa_supply: IconEvaaSupply,
    evaa_withdraw: IconStakeWithdraw,
    evaa_liquidate: IconSmartContractExec,
    gasless_request: IconStakeIn,
    change_wallet_key: IconKeyRound,
});

export default {
    props: {
        event: String,
        traceActions: Array,
        traceStatus: Boolean,
        isPending: Boolean,
        isTxSimple: Boolean,
        isTickTock: Boolean,
    },

    data() {
        return {
            nftError: false,
        };
    },

    computed: {
        getIcon() {
            if (this.traceStatus === undefined) return null;
            if (!this.isTxSimple) {
                return this.traceStatus ? IconSuccess : IconFailed;
            }
            const type = this.traceActions[0]?.type;
            return iconMap[type] || IconUnknown;
        },
        simplePreview() {
            return this.traceActions && this.traceActions[0]?.simple_preview;
        },
    },

    components: {
        UiInlineNft,
        IconNft,
        IconService,
    },
};
</script>

<style lang="scss">
.tx-mobile-overview {
    position: relative;
    display: inline-flex;
    margin: 14px 0 2px;
}

// Neutral raised disc with the action glyph; the status pill underneath carries the colour:
.tx-mobile-overview-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    box-sizing: border-box;
    color: var(--body-text-color);
    background: var(--tab-thumb-background);
    box-shadow: var(--tab-thumb-shadow);

    svg {
        width: 28px;
        height: 28px;
    }

    &--skeleton {
        color: transparent;
        background: var(--body-light-muted-color);
        box-shadow: none;
    }

    &--failed {
        color: var(--red-bright);
    }
}

.tx-mobile-overview-nft {
    width: 128px;
    height: 128px;
    border-radius: 16px;
    margin: 0;
    background-color: var(--badge-grey-background-color);
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        color: var(--body-text-color);
        width: 42px;
        height: 42px
    }
}
.tx-mobile-overview-swap {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
}
.tx-mobile-overview-swap-logo {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    border: 5px solid var(--card-background);
    background-color: var(--card-background);

    &--left {
        z-index: 1;
        transform: translateX(25px);
    }
    &--right {
        z-index: 2;
        transform: translateX(-25px);
    }
}
.tx-mobile-overview-swap-arrow {
    width: 22px;
    height: 22px;
    color: var(--body-muted-text-color);
}
</style>
